import { cn } from '@/lib/utils';

interface MarqueeItem {
  label: string;
  icon?: string;
}

interface InfiniteMarqueeProps {
  items: MarqueeItem[];
  duration?: number; // seconds for one full cycle
  direction?: 'left' | 'right';
  className?: string;
}

const InfiniteMarquee = ({ items, duration = 40, direction = 'left', className }: InfiniteMarqueeProps) => {
  // Duplicate items for seamless loop
  const doubled = [...items, ...items];

  return (
    <div className={cn('overflow-hidden relative group', className)}>
      {/* Fade masks on edges */}
      <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-16 md:w-24 z-10 bg-gradient-to-r from-[#FAFBFC] dark:from-secondary-950 to-transparent pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-16 md:w-24 z-10 bg-gradient-to-l from-[#FAFBFC] dark:from-secondary-950 to-transparent pointer-events-none" />

      <div
        className={cn(
          "flex gap-3 w-max",
          direction === 'left' ? 'animate-marquee-left' : 'animate-marquee-right',
          "group-hover:[animation-play-state:paused]"
        )}
        style={{ animationDuration: `${duration}s` }}
      >
        {doubled.map((item, i) => (
          <div
            key={`${item.label}-${i}`}
            className={cn(
              "flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-full shrink-0",
              "bg-white shadow-sm dark:shadow-none dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06]",
              "hover:bg-primary-500/10 hover:border-primary-500/30",
              "transition-colors duration-300 cursor-default select-none"
            )}
          >
            {item.icon && <span className="text-base leading-none">{item.icon}</span>}
            <span className="text-xs sm:text-sm text-gray-600 dark:text-secondary-300 whitespace-nowrap">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export { InfiniteMarquee };
export type { MarqueeItem };
